import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ArrowRightLeft, ChevronDown, ChevronRight, RefreshCw, ShieldAlert } from 'lucide-react'
import { useApplyOwnerTransferAction, useGetOwnerTransfers, useTransferSigning } from '../hooks/backend/owners'
import { useCapabilities } from '../hooks/useCapabilities'
import { Button } from '../lib/shadcn/button'
import { Spinner } from '../lib/shadcn/spinner'
import { toast } from '../lib/shadcn/sonner'
import { cn } from '../lib/shadcn/utils'
import { writeErrorMessage } from '../utils/writeError'
import { DocumentButtons, OwnerDocUpload, ReadingView, type Upload } from '../components/owners/OwnerPipelineDialog'
import { UploadControl } from '../components/documents/UploadControl'
import { useOwnerMailIntake } from '../hooks/backend/owners'

// Owner Transfers, 25 September 2026 (db/migrations/0292). Every old-model car's
// offer to move to the new model (from 1 October 2026; since 9400, from the
// first of the month after acceptance). Every figure on this page
// was frozen on the offer when it was prepared, from issued owner statements,
// and the retention plan was worked out by owner_transfer_retention_plan_v1.
// The page formats and computes nothing; a missing figure shows as a dash.

type PlanRow = {
  month: number
  period: string
  retention_open: number
  interest: number
  recovered: number
  paid_back: number
  retention_close: number
  lpg_floor: number
  instalment_to_bank: number
  to_owner: number
}

type Offer = {
  id: string
  vehicle_id: string | null
  person_id: string | null
  new_application_id: string | null
  // Who pays the finance house on the new model (0352).
  instalment_paid_by: 'fleetly' | 'owner'
  // The first day of this transfer's first month (9400). Fixed when the owner
  // accepts; until then it rolls to the month after today.
  commencement: string | null
  status: 'draft' | 'offered' | 'accepted' | 'declined' | 'contract_sent' | 'signed' | 'transferred'
  owner_name: string
  email: string | null
  plate: string
  car_label: string
  avg_net: number | null
  fill_pct: number | null
  offline_days: number | null
  months_count: number
  retention_balance: number
  retention_as_of: string | null
  retention_after_scan: number
  instalment: number
  financed: boolean
  // False when the instalment is above R7,700: Send offer then sends the owner
  // their figures and the reason, and the page has no Accept.
  qualifies: boolean
  // What the owner chose on the page (0295): 'stay' and 'offboard' both close
  // the offer; 'offboard' needs Luyanda to arrange the handback.
  owner_choice: 'transfer' | 'stay' | 'offboard' | null
  plan: { rows: PlanRow[]; total_to_owner: number; total_interest: number; debt_left_at_end: number }
  offered_at: string | null
  accepted_at: string | null
  declined_at: string | null
  decline_reason: string | null
  last_email: { status: string; queued_at: string; sent_at: string | null } | null
  // SignNow (0297): the filled agreement, and the owner's debit order form.
  agreement_status: 'preparing' | 'prepared' | 'invited' | 'failed' | 'signed' | null
  agreement_error: string | null
  debit_order: { id: string; status: string; missing: string[] | null; error: string | null } | null
  // The finance house statement the owner uploaded, with its reading, and what
  // the automatic check made of it (0320): 'send' went out by itself, 'hold'
  // waits here with the reasons, 'approved' took the statement's instalment.
  statement: Upload | null
  statement_check: {
    decision: 'send' | 'hold' | 'already_sent' | 'approved'
    reasons: string[]
    instalment_statement: number | null
    instalment_offer: number | null
    checked_at: string
  } | null
  // Sent without the statement (6900): who let it, when and why. The finance
  // account and settlement blanks are typed by Fleetly at signing.
  statement_waived: { at: string; by: string | null; reason: string } | null
  // The contract pull (0322): what the agreement is still missing. Critical
  // items block sending; 'decide_once' ones are programme terms set once for
  // every agreement.
  contract: {
    critical: { code: string; text: string; group?: 'decide_once' }[]
    warnings: string[]
    ready: boolean
    pulled_at: string
  } | null
  flags: string[]
}

// "2026-11-01" to "1 November 2026". The date is the database's; a missing one
// says so rather than guessing October.
function startDay(iso: string | null | undefined): string {
  if (!iso) return 'the start date'
  return new Date(`${iso.slice(0, 10)}T12:00:00+02:00`).toLocaleDateString('en-ZA', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Africa/Johannesburg',
  })
}

type Board = {
  terms: { offer_closes: string | null; commencement: string; bolt_scan_cost_zar: number; placeholders: string[] }
  can_act: boolean
  eligible_without_offer: number
  offers: Offer[]
}

const STATUS_LABEL: Record<Offer['status'], string> = {
  draft: 'Not sent',
  offered: 'Offer sent',
  accepted: 'Accepted',
  declined: 'Declined',
  contract_sent: 'Agreement sent',
  signed: 'Signed',
  transferred: 'Transferred',
}

const STATUS_TONE: Record<Offer['status'], string> = {
  draft: 'bg-muted text-muted-foreground',
  offered: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  accepted: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200',
  declined: 'bg-muted text-muted-foreground line-through',
  contract_sent: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200',
  signed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
  transferred: 'bg-emerald-600 text-white',
}

function zar(n: number | null | undefined, cents = false): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return (
    'R' +
    Number(n).toLocaleString('en-ZA', { minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0 })
  )
}

// The one next step for each status. Withdraw is offered separately.
const NEXT: Partial<Record<Offer['status'], { action: string; label: string; confirm: string }>> = {
  draft: {
    action: 'send_offer',
    label: 'Send offer',
    confirm: 'This emails the transfer offer, with the transfer pack attached, to the owner now. Send it?',
  },
  offered: {
    action: 'resend_offer',
    label: 'Resend',
    confirm: 'This emails the offer again with a new link. Older links keep working. Resend it?',
  },
  contract_sent: { action: 'mark_signed', label: 'Signed', confirm: 'Record that every party has signed?' },
  signed: {
    action: 'complete_transfer',
    label: 'Complete transfer',
    confirm: 'Complete the transfer? The car moves to the new model on the board. Its statement moves in the payout step.',
  },
}

export default function OwnerTransfers() {
  const { loading: accessLoading, canView, caption } = useCapabilities()
  const board = useGetOwnerTransfers()
  const act = useApplyOwnerTransferAction()
  const trigger = board.trigger
  const [open, setOpen] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(() => {
    trigger({}, { skipCache: true })
  }, [trigger])
  useEffect(() => {
    if (canView) load()
  }, [canView, load])

  const data = board.data as Board | null | undefined
  const offers = useMemo(() => (data && Array.isArray(data.offers) ? data.offers : []), [data])
  const counts = useMemo(() => {
    const c: Partial<Record<Offer['status'], number>> = {}
    for (const o of offers) c[o.status] = (c[o.status] ?? 0) + 1
    return c
  }, [offers])

  const run = async (offerId: string | null, action: string, confirmText: string, note?: string | null) => {
    if (!window.confirm(confirmText)) return
    setBusy(offerId ?? action)
    try {
      await act.trigger({ offer_id: offerId, action, note: note ?? null }).result
      toast.success('Done')
      load()
    } catch (e) {
      toast.error(writeErrorMessage(e))
    } finally {
      setBusy(null)
    }
  }

  // The SignNow steps run in the owner-onboard function. The agreement is built
  // in the background, so the board is reloaded a little later to show it.
  const signing = useTransferSigning()
  const sign = async (
    o: Offer,
    action: 'prepare_agreement' | 'send_agreement' | 'debit_order_view' | 'debit_order_send' | 'draft_agreement' | 'refresh_contract',
    confirmText?: string,
  ) => {
    if (confirmText && !window.confirm(confirmText)) return
    setBusy(o.id)
    try {
      const r = (await signing.trigger({
        action,
        offer_id: o.id,
        form_id: o.debit_order?.id ?? null,
      }).result) as { url?: string } | null
      if ((action === 'debit_order_view' || action === 'draft_agreement') && r?.url) window.open(r.url, '_blank', 'noopener')
      else if (action === 'refresh_contract') toast.success("Reading the car's documents. The board updates in a minute or two.")
      else toast.success(action.endsWith('agreement') ? 'Working on it. The board updates in a minute.' : 'Sent to sign')
      if (action === 'prepare_agreement' || action === 'send_agreement' || action === 'refresh_contract') window.setTimeout(load, 60000)
      load()
    } catch (e) {
      toast.error(writeErrorMessage(e))
    } finally {
      setBusy(null)
    }
  }

  const withdraw = (o: Offer) => {
    const why = window.prompt(`Why is the offer for ${o.plate} withdrawn?`)
    if (!why || !why.trim()) return
    void run(o.id, 'withdraw', `Withdraw the offer for ${o.plate}? Its links stop working.`, why.trim())
  }

  const waiveStatement = (o: Offer) => {
    const why = window.prompt(
      `${o.owner_name}, ${o.plate}. Why does the agreement go out without the finance house statement? The reason is kept with the transfer.`,
    )
    if (!why || !why.trim()) return
    void run(
      o.id,
      'waive_statement',
      `Send without the statement? The agreement uses the offer's instalment of ${zar(o.instalment, true)}, and the finance account number and settlement balance are left blank for Fleetly to type when signing on SignNow. Then use Send agreement to sign.`,
      why.trim(),
    )
  }

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }
  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <ShieldAlert className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">Owner Transfers needs a cockpit login with view access.</p>
          <p className="text-xs text-muted-foreground">Ask an admin to add you in cockpit access.</p>
        </div>
      </div>
    )
  }

  const canAct = Boolean(data?.can_act)

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="border-b border-border px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
              <ArrowRightLeft className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-foreground">Owner Transfers</h1>
              <p className="text-xs text-muted-foreground">
                Old model to Manage My Car. An owner who accepts now starts on {startDay(data?.terms?.commencement)}; each
                transfer starts on the first of the month after it is accepted. {caption}
              </p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              {canAct && (data?.eligible_without_offer ?? 0) > 0 && (
                <Button
                  size="sm"
                  className="h-8"
                  disabled={busy !== null}
                  onClick={() =>
                    run(
                      null,
                      'prepare_all',
                      `Prepare offers for the ${data?.eligible_without_offer} active old-model cars without one? This sends nothing.`,
                    )
                  }
                >
                  Prepare offers ({data?.eligible_without_offer})
                </Button>
              )}
              <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={load} disabled={board.loading}>
                {board.loading ? <Spinner className="h-3.5 w-3.5" /> : <RefreshCw className="h-3.5 w-3.5" />}
                Refresh
              </Button>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {(Object.keys(STATUS_LABEL) as Offer['status'][]).map((s) => (
              <span key={s} className={cn('rounded-full px-2.5 py-1 font-medium', STATUS_TONE[s])}>
                {STATUS_LABEL[s]} <span className="tabular-nums">{counts[s] ?? 0}</span>
              </span>
            ))}
          </div>
          {data?.terms?.placeholders && data.terms.placeholders.length > 0 && (
            <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Still placeholders in owner_transfer.terms: {data.terms.placeholders.join(', ')}. The debit order form
              cannot be completed until they are set.
            </p>
          )}
        </div>

        <EmailedDocuments canAct={canAct} onFiled={load} />

        {board.loading && offers.length === 0 ? (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading transfers…
          </div>
        ) : offers.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">
            No offers yet. {canAct ? 'Prepare offers builds one per active old-model car and sends nothing.' : ''}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Owner and car</th>
                  <th className="px-3 py-2 text-right font-medium">Avg net</th>
                  <th className="px-3 py-2 text-right font-medium">Filled</th>
                  <th className="px-3 py-2 text-right font-medium">Days off</th>
                  <th className="px-3 py-2 text-right font-medium">Retention</th>
                  <th className="px-3 py-2 text-right font-medium">Instalment</th>
                  <th className="px-3 py-2 text-right font-medium">12 months to owner</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {offers.map((o) => {
                  const next =
                    o.qualifies === false && (o.status === 'draft' || o.status === 'offered')
                      ? {
                          action: o.status === 'draft' ? 'send_offer' : 'resend_offer',
                          label: o.status === 'draft' ? 'Send figures' : 'Resend figures',
                          confirm:
                            'This car does not qualify yet. This emails the owner their figures and says a lower instalment can be considered. There is no Accept. Send it?',
                        }
                      : NEXT[o.status]
                  const expanded = open === o.id
                  return (
                    <Fragment key={o.id}>
                      <tr className="border-t border-border align-top">
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            className="flex items-start gap-1 text-left"
                            onClick={() => setOpen(expanded ? null : o.id)}
                          >
                            {expanded ? (
                              <ChevronDown className="mt-0.5 h-4 w-4 shrink-0" />
                            ) : (
                              <ChevronRight className="mt-0.5 h-4 w-4 shrink-0" />
                            )}
                            <span>
                              <span className="font-medium text-foreground">{o.owner_name}</span>
                              <span className="block text-xs text-muted-foreground">
                                {o.plate} · {o.car_label}
                                {o.email ? '' : ' · no email'}
                              </span>
                            </span>
                          </button>
                          {o.flags.length > 0 && (
                            <ul className="mt-1 space-y-0.5 pl-5 text-xs text-amber-700 dark:text-amber-300">
                              {o.flags.map((f) => (
                                <li key={f}>{f}</li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{zar(o.avg_net)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{o.fill_pct == null ? '—' : `${o.fill_pct}%`}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{o.offline_days ?? '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          <span className={cn(o.retention_after_scan < 0 && 'text-red-600 dark:text-red-400')}>
                            {zar(o.retention_after_scan, true)}
                          </span>
                          <span className="block text-[11px] text-muted-foreground">
                            {zar(o.retention_balance, true)} {o.retention_as_of ?? ''}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{o.financed ? zar(o.instalment, true) : 'Paid off'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {zar(o.plan?.total_to_owner, true)}
                          {o.plan?.debt_left_at_end > 0 && (
                            <span className="block text-[11px] text-red-600 dark:text-red-400">
                              {zar(o.plan.debt_left_at_end, true)} still owed
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {o.owner_choice === 'offboard' ? (
                            <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-950 dark:text-red-200">
                              Wants to offboard
                            </span>
                          ) : o.owner_choice === 'stay' ? (
                            <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', STATUS_TONE.draft)}>Staying on current model</span>
                          ) : (
                            <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', STATUS_TONE[o.status])}>
                              {STATUS_LABEL[o.status]}
                            </span>
                          )}
                          {o.last_email && (
                            <span className="mt-1 block text-[11px] text-muted-foreground">
                              Email {o.last_email.status}
                              {o.last_email.sent_at ? ` ${new Date(o.last_email.sent_at).toLocaleDateString('en-ZA')}` : ''}
                            </span>
                          )}
                          {o.agreement_status && (
                            <span
                              className={cn(
                                'mt-1 block max-w-[16rem] text-[11px]',
                                o.agreement_status === 'failed' ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground',
                              )}
                            >
                              Agreement {o.agreement_status === 'invited' ? 'out to sign' : o.agreement_status}
                              {o.agreement_status === 'failed' && o.agreement_error ? `: ${o.agreement_error}` : ''}
                            </span>
                          )}
                          {o.debit_order && (
                            <span
                              className={cn(
                                'mt-1 block max-w-[16rem] text-[11px]',
                                o.debit_order.status === 'failed' ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground',
                              )}
                            >
                              Debit order form {o.debit_order.status === 'review' ? 'filled, to check' : o.debit_order.status}
                              {o.debit_order.missing && o.debit_order.missing.length > 0
                                ? `. Not on file: ${o.debit_order.missing.join(', ')}`
                                : ''}
                              {o.debit_order.status === 'failed' && o.debit_order.error ? `: ${o.debit_order.error}` : ''}
                            </span>
                          )}
                          {o.financed && (
                            <span className="mt-1 block max-w-[16rem] text-[11px] text-muted-foreground">
                              {o.instalment_paid_by === 'owner'
                                ? 'Owner keeps paying the finance house; Fleetly pays the full R7,700'
                                : 'Fleetly pays the instalment to the finance house'}
                            </span>
                          )}
                          {o.decline_reason && (
                            <span className="mt-1 block max-w-[16rem] text-[11px] text-muted-foreground">{o.decline_reason}</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {canAct && (
                            <div className="flex flex-wrap justify-end gap-1.5">
                              {next && (
                                <Button
                                  size="sm"
                                  className="h-7 px-2 text-xs"
                                  variant={o.status === 'offered' ? 'outline' : 'default'}
                                  disabled={busy !== null || ((next.action === 'send_offer' || next.action === 'resend_offer') && !o.email)}
                                  onClick={() => run(o.id, next.action, `${o.owner_name}, ${o.plate}. ${next.confirm}`)}
                                >
                                  {busy === o.id ? <Spinner className="h-3 w-3" /> : next.label}
                                </Button>
                              )}
                              {(o.status === 'accepted' || o.status === 'contract_sent') && o.agreement_status !== 'invited' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 text-xs"
                                    disabled={busy !== null || o.agreement_status === 'preparing'}
                                    onClick={() => sign(o, 'prepare_agreement')}
                                  >
                                    Prepare agreement
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="h-7 px-2 text-xs"
                                    disabled={busy !== null || o.agreement_status === 'preparing'}
                                    onClick={() =>
                                      sign(
                                        o,
                                        'send_agreement',
                                        `${o.owner_name}, ${o.plate}. Fill the agreement from our records and send it on SignNow: Fleetly first, then the owner, the two witnesses and LeaseCo. Send it?`,
                                      )
                                    }
                                  >
                                    Send agreement to sign
                                  </Button>
                                </>
                              )}
                              {o.debit_order && o.debit_order.status === 'review' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 text-xs"
                                    disabled={busy !== null}
                                    onClick={() => sign(o, 'debit_order_view')}
                                  >
                                    Check debit order form
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="h-7 px-2 text-xs"
                                    disabled={busy !== null}
                                    onClick={() =>
                                      sign(
                                        o,
                                        'debit_order_send',
                                        `${o.owner_name}, ${o.plate}. Have you checked the filled debit order form? It goes to the owner and then to Fleetly to sign.`,
                                      )
                                    }
                                  >
                                    Send debit order to sign
                                  </Button>
                                </>
                              )}
                              {o.financed &&
                                (o.status === 'draft' || o.status === 'offered' || o.status === 'accepted') &&
                                o.agreement_status !== 'invited' &&
                                o.agreement_status !== 'preparing' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 text-xs"
                                    disabled={busy !== null}
                                    onClick={() =>
                                      run(
                                        o.id,
                                        o.instalment_paid_by === 'owner' ? 'fleetly_pays_instalment' : 'owner_pays_instalment',
                                        o.instalment_paid_by === 'owner'
                                          ? `${o.owner_name}, ${o.plate}. Fleetly pays the ${zar(o.instalment, true)} instalment to the finance house from ${startDay(o.commencement)}, the debit order moves to Fleetly's account, and the owner is paid the balance. Change it?`
                                          : `${o.owner_name}, ${o.plate}. The owner keeps paying the ${zar(o.instalment, true)} instalment to the finance house, and Fleetly pays the full R7,700. No debit order moves. Change it?`,
                                      )
                                    }
                                  >
                                    {o.instalment_paid_by === 'owner' ? 'Fleetly pays the instalment' : 'Owner pays the instalment'}
                                  </Button>
                                )}
                              {o.status !== 'transferred' && o.status !== 'declined' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-xs text-muted-foreground"
                                  disabled={busy !== null}
                                  onClick={() => withdraw(o)}
                                >
                                  Withdraw
                                </Button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="border-t border-dashed border-border bg-muted/20">
                          <td colSpan={9} className="px-3 py-3">
                            <PlanTable offer={o} />
                          </td>
                        </tr>
                      )}
                      {(o.status === 'accepted' || o.status === 'contract_sent') && (
                        <tr className="bg-muted/10">
                          <td colSpan={9} className="px-3 pb-3 pt-0">
                            <ContractStrip
                              offer={o}
                              canAct={canAct}
                              busy={busy !== null}
                              onDraft={() => sign(o, 'draft_agreement')}
                              onRefresh={() => sign(o, 'refresh_contract')}
                            />
                            {o.financed && (
                              <StatementStrip
                                offer={o}
                                canAct={canAct}
                                busy={busy !== null}
                                onChanged={load}
                                onWaive={() => waiveStatement(o)}
                                onRequire={() =>
                                  run(
                                    o.id,
                                    'require_statement',
                                    `${o.owner_name}, ${o.plate}. Wait for the finance house statement again before the agreement goes out?`,
                                  )
                                }
                                onUseInstalment={() =>
                                  run(
                                    o.id,
                                    'use_statement_instalment',
                                    `${o.owner_name}, ${o.plate}. Use the statement's instalment of ${zar(o.statement_check?.instalment_statement, true)} instead of ${zar(o.instalment, true)}? The 12-month plan is worked out again on it, so what the owner is paid each month changes. Have you spoken to them?`,
                                  )
                                }
                              />
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// What the owner email reader (0334) found in luyanda@ and admin@: filed to an
// application or a car, or waiting for a person, with the reason.
type MailItem = {
  id: string
  mailbox: string
  received_at: string | null
  from_email: string | null
  subject: string | null
  filename: string | null
  status: 'filed' | 'filed_vehicle' | 'needs_a_person'
  doc_kind: string | null
  reason: string | null
  owner_name: string | null
  resolved_at: string | null
}

function EmailedDocuments({ canAct, onFiled }: { canAct: boolean; onFiled: () => void }) {
  const mail = useOwnerMailIntake()
  const trigger = mail.trigger
  const [items, setItems] = useState<MailItem[]>([])
  const [busy, setBusy] = useState<string | null>(null)
  const load = useCallback(async () => {
    try {
      const r = (await trigger({ action: 'list' }, { skipCache: true }).result) as MailItem[] | null
      setItems(Array.isArray(r) ? r : [])
    } catch {
      setItems([])
    }
  }, [trigger])
  useEffect(() => {
    void load()
  }, [load])
  const open = items.filter((i) => i.status === 'needs_a_person' && !i.resolved_at)
  const filed = items.filter((i) => i.status !== 'needs_a_person')
  if (items.length === 0) return null
  const view = async (id: string) => {
    const tab = window.open('about:blank', '_blank')
    try {
      const r = (await trigger({ action: 'view', id }).result) as { url?: string } | null
      if (r?.url && tab) tab.location.href = r.url
      else tab?.close()
    } catch (e) {
      tab?.close()
      toast.error(writeErrorMessage(e))
    }
  }
  const resolve = async (id: string) => {
    setBusy(id)
    try {
      await trigger({ action: 'resolve', id }).result
      await load()
      onFiled()
    } catch (e) {
      toast.error(writeErrorMessage(e))
    } finally {
      setBusy(null)
    }
  }
  return (
    <div className="border-b border-border px-4 py-3 text-sm">
      <p className="font-medium">Documents owners emailed</p>
      <p className="text-xs text-muted-foreground">
        Read every 15 minutes from luyanda@ and admin@, only mail from owners with an onboarding or a transfer going ahead.
        {filed.length > 0 ? ` ${filed.length} filed automatically in the last 60 days.` : ''}
      </p>
      {open.length > 0 && (
        <ul className="mt-2 divide-y divide-border rounded-md border border-border">
          {open.map((i) => (
            <li key={i.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-xs">
              <span className="min-w-0 flex-1">
                <span className="font-medium">{i.owner_name ?? i.from_email}</span>: {i.filename ?? 'attachment'}
                <span className="block text-muted-foreground">{i.reason}</span>
              </span>
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => view(i.id)}>
                Open
              </Button>
              {canAct && (
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" disabled={busy === i.id} onClick={() => resolve(i.id)}>
                  Dealt with
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// What the agreement is still missing, from owner_transfer_contract_pull_v1.
// Nothing here is typed into the contract: each line says where the answer
// comes from, and once it is there the box fills itself.
function ContractStrip({
  offer,
  canAct,
  busy,
  onDraft,
  onRefresh,
}: {
  offer: Offer
  canAct: boolean
  busy: boolean
  onDraft: () => void
  onRefresh: () => void
}) {
  const c = offer.contract
  if (!c) return null
  const perCar = c.critical.filter((x) => x.group !== 'decide_once')
  const once = c.critical.filter((x) => x.group === 'decide_once')
  return (
    <div className="mb-2 rounded-md border border-border bg-card px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1 font-medium">Agreement details</span>
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-[11px] font-medium',
            c.ready
              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
              : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
          )}
        >
          {c.ready ? 'Every box filled' : `${c.critical.length} missing`}
        </span>
        {canAct && (
          <>
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={busy} onClick={onDraft}>
              View draft
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" disabled={busy} onClick={onRefresh}>
              Read the car's documents again
            </Button>
            {offer.new_application_id && offer.instalment_paid_by !== 'owner' && (
              <OwnerDocUpload applicationId={offer.new_application_id} docKind="debit_order_form" onDone={onRefresh} label="Upload a debit order form" />
            )}
          </>
        )}
      </div>
      {canAct && offer.vehicle_id && (
        <details className="mt-1 text-xs">
          <summary className="cursor-pointer text-muted-foreground">
            Add a car document the owner sent (licence disc, RC1, roadworthy, tracking, insurance)
          </summary>
          <div className="mt-2">
            <UploadControl
              categories={[
                { code: 'registration_licensing', label: 'Registration and licensing' },
                { code: 'insurance_tracking', label: 'Insurance and tracking' },
                { code: 'onboarding', label: 'Onboarding documents' },
              ]}
              scope={{ vehicle_id: offer.vehicle_id }}
              onUploaded={onRefresh}
            />
            <p className="text-[11px] text-muted-foreground">
              It is read automatically: the agreement takes the engine number, the disc expiry and the rest from it.
            </p>
          </div>
        </details>
      )}
      {perCar.length > 0 && (
        <ul className="mt-1 list-disc pl-4 text-xs text-amber-800 dark:text-amber-200">
          {perCar.map((x) => (
            <li key={x.code}>{x.text}</li>
          ))}
        </ul>
      )}
      {once.length > 0 && (
        <p className="mt-1 text-xs text-muted-foreground">
          Decided once for every agreement, still open:{' '}
          {once.map((x) => x.text.replace(/^Programme term not decided: /, '')).join('; ')}
        </p>
      )}
      {c.warnings.length > 0 && (
        <ul className="mt-1 list-disc pl-4 text-xs text-muted-foreground">
          {c.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

// The statement, on the board as on the Pipeline panel: View, Accept, Reject,
// the reading, Read again and Password, then what the automatic check decided.
function StatementStrip({
  offer,
  canAct,
  busy,
  onChanged,
  onWaive,
  onRequire,
  onUseInstalment,
}: {
  offer: Offer
  canAct: boolean
  busy: boolean
  onChanged: () => void
  onWaive: () => void
  onRequire: () => void
  onUseInstalment: () => void
}) {
  const waived = offer.statement_waived
  // The waiver only matters while no statement has been accepted, and only
  // before the agreement is out.
  const notOut = offer.status === 'accepted' && offer.agreement_status !== 'invited' && offer.agreement_status !== 'preparing'
  const up = offer.statement
  const chk = offer.statement_check
  const differs =
    chk?.instalment_statement != null && Math.abs(Number(chk.instalment_statement) - Number(offer.instalment)) > 1
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1 font-medium">
          Finance house statement
          {up?.filename && <span className="ml-2 text-xs font-normal text-muted-foreground">{up.filename}</span>}
        </span>
        {canAct && offer.new_application_id && up?.review_state !== 'accepted' && (
          <OwnerDocUpload applicationId={offer.new_application_id} docKind="finance_statement" onDone={onChanged} label="Upload a statement" />
        )}
        {!up ? (
          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">Not uploaded yet</span>
        ) : (
          <>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-[11px] font-medium',
                up.review_state === 'accepted'
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                  : up.review_state === 'rejected'
                    ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200'
                    : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
              )}
            >
              {up.review_state === 'accepted' ? 'Accepted' : up.review_state === 'rejected' ? 'Rejected' : 'Waiting for review'}
            </span>
            <DocumentButtons upload={up} canAct={canAct} onChanged={onChanged} />
          </>
        )}
      </div>
      {up && (
        <div className="mt-1">
          <ReadingView upload={up} onChanged={onChanged} />
        </div>
      )}
      {chk && chk.decision === 'send' && (
        <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">
          Every check passed, so the statement was accepted and the agreement went out by itself.
        </p>
      )}
      {chk && chk.decision === 'hold' && chk.reasons.length > 0 && (
        <div className="mt-1 text-xs">
          <p className="font-medium text-amber-700 dark:text-amber-300">
            Held for your approval. Accept it and send the agreement yourself, or reject it and the owner is asked for another.
          </p>
          <ul className="mt-0.5 list-disc pl-4 text-muted-foreground">
            {chk.reasons.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      )}
      {chk && chk.decision === 'approved' && (
        <p className="mt-1 text-xs text-muted-foreground">
          The plan now uses the statement's instalment of {zar(offer.instalment, true)}.
        </p>
      )}
      {waived && up?.review_state !== 'accepted' && (
        <div className="mt-1 text-xs">
          <p className="font-medium text-amber-700 dark:text-amber-300">
            Going out without the statement{waived.by ? `, by ${waived.by}` : ''},{' '}
            {new Date(waived.at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', timeZone: 'Africa/Johannesburg' })}:{' '}
            {waived.reason}
          </p>
          <p className="text-muted-foreground">
            The agreement uses the offer's instalment of {zar(offer.instalment, true)}. Type the finance account number and
            settlement balance when Fleetly signs on SignNow.
          </p>
        </div>
      )}
      {canAct && notOut && up?.review_state !== 'accepted' && (
        <Button
          size="sm"
          variant="outline"
          className="mt-2 h-7 px-2 text-xs"
          disabled={busy}
          onClick={waived ? onRequire : onWaive}
        >
          {waived ? 'Wait for the statement again' : 'Send without the statement'}
        </Button>
      )}
      {canAct && differs && up?.review_state === 'accepted' && offer.status === 'accepted' && (
        <Button size="sm" variant="outline" className="mt-2 h-7 px-2 text-xs" disabled={busy} onClick={onUseInstalment}>
          Use the statement's instalment, {zar(chk?.instalment_statement, true)}
        </Button>
      )}
    </div>
  )
}

function PlanTable({ offer }: { offer: Offer }) {
  const rows = offer.plan?.rows ?? []
  return (
    <div className="overflow-x-auto">
      <p className="mb-2 text-xs text-muted-foreground">
        The plan the owner sees on their transfer page. Retention after the R249 Bolt inspection:{' '}
        {zar(offer.retention_after_scan, true)}. 2% a month on a positive balance for months 1 to 6, paid with the R7,700;
        equal releases from month 7, never below the LPG recovery for that month. A negative balance is recovered over 12
        months without taking a payment below zero.
      </p>
      <table className="w-full text-xs tabular-nums">
        <thead className="text-muted-foreground">
          <tr>
            <th className="px-2 py-1 text-left font-medium">Month</th>
            <th className="px-2 py-1 text-right font-medium">Retention held</th>
            <th className="px-2 py-1 text-right font-medium">2%</th>
            <th className="px-2 py-1 text-right font-medium">Recovered</th>
            <th className="px-2 py-1 text-right font-medium">Released</th>
            <th className="px-2 py-1 text-right font-medium">LPG floor</th>
            <th className="px-2 py-1 text-right font-medium">Instalment</th>
            <th className="px-2 py-1 text-right font-medium">To owner</th>
            <th className="px-2 py-1 text-right font-medium">Retention after</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.month} className="border-t border-border/60">
              <td className="px-2 py-1">{r.period}</td>
              <td className="px-2 py-1 text-right">{zar(r.retention_open, true)}</td>
              <td className="px-2 py-1 text-right">{r.interest ? zar(r.interest, true) : '—'}</td>
              <td className="px-2 py-1 text-right">{r.recovered ? zar(r.recovered, true) : '—'}</td>
              <td className="px-2 py-1 text-right">{r.paid_back ? zar(r.paid_back, true) : '—'}</td>
              <td className="px-2 py-1 text-right">{zar(r.lpg_floor)}</td>
              <td className="px-2 py-1 text-right">{zar(r.instalment_to_bank, true)}</td>
              <td className="px-2 py-1 text-right font-semibold">{zar(r.to_owner, true)}</td>
              <td className="px-2 py-1 text-right">{zar(r.retention_close, true)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
